import React from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { Box } from '@mui/material';
import type { ChartDataPoint, MetricType } from '../types';
import { CONFIG } from '../constants';
import { currencyFormatter } from '../utils';

interface ChartRendererProps {
    chartData: ChartDataPoint[];
    tickValues: number[];
    metric: MetricType;
    theme: any;
}

export const ChartRenderer: React.FC<ChartRendererProps> = ({
    chartData,
    tickValues,
    metric,
    theme,
}) => {
    const hasNoVisibleData = chartData.length > 0 && chartData.every(item => item.value === 0);

    return (
        <>
            <ResponsiveBar
                data={chartData}
                keys={['value']}
                indexBy="id"
                valueFormat={value =>
                    metric === 'amount' ? currencyFormatter(value, true) : value.toString()
                }
                margin={CONFIG.CHART_MARGINS}
                padding={0.3}
                indexScale={{ type: 'band', round: true }}
                valueScale={{
                    type: 'linear',
                    min: 0,
                    max:
                        chartData.length === 0 || chartData.every(item => item.value === 0)
                            ? CONFIG.SCALE_MAX_VALUES[metric]
                            : 'auto',
                }}
                colors={[theme.palette.chart.main]}
                enableGridX={false}
                enableGridY={false}
                enableLabel={false}
                axisLeft={{
                    tickSize: 4,
                    tickPadding: 4,
                    tickRotation: 0,
                    legend: null,
                    legendPosition: 'middle',
                    legendOffset: -40,
                    format: value =>
                        metric === 'amount' ? currencyFormatter(value, true) : value.toString(),
                    tickValues: tickValues,
                }}
                axisBottom={{
                    tickSize: 4,
                    tickPadding: 4,
                    tickRotation: 0,
                    legend: null,
                    legendPosition: 'middle',
                    legendOffset: 32,
                    format: value => {
                        const item = chartData.find(d => d.id === value);
                        return item?.label || value;
                    },
                }}
                borderRadius={2}
                animate={true}
                motionConfig="gentle"
                theme={{
                    background: 'transparent',
                    textColor: theme.palette.text.secondary,
                    fontSize: 11,
                    fontFamily: theme.typography.fontFamily,
                    axis: {
                        domain: {
                            line: {
                                stroke: theme.palette.divider,
                                strokeWidth: 1,
                            },
                        },
                        ticks: {
                            line: {
                                stroke: theme.palette.divider,
                                strokeWidth: 1,
                            },
                        },
                    },
                    grid: {
                        line: {
                            stroke: theme.palette.divider,
                            strokeWidth: 0.5,
                        },
                    },
                }}
                tooltip={({ data }) => (
                    <div
                        style={{
                            width: 120,
                            background: '#ffffff',
                            padding: '9px 12px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            fontSize: '12px',
                            fontFamily: theme.typography.fontFamily,
                            zIndex: 10000,
                            position: 'absolute',
                        }}
                    >
                        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{data.id}</div>
                        <div style={{ color: '#666' }}>{data.tooltipLabel}</div>
                    </div>
                )}
            />

            {hasNoVisibleData && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: '35%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        color: 'text.secondary',
                        fontSize: '0.75rem',
                        fontStyle: 'italic',
                        pointerEvents: 'none',
                        zIndex: 10,
                        whiteSpace: 'nowrap',
                    }}
                >
                    No funding activity here
                </Box>
            )}
        </>
    );
};
